import React from "react";
import { ApiError } from "store/handleResponseError";
import { useUploadFileMutation, useUploadMutation } from "store/reducers";

import { compressToJpeg } from "./compressToJpeg";

const MAX_PDF_SIZE = 5 * 1024 * 1024;

async function prepareFile(file: File): Promise<File> {
  // Some platforms report an empty or nonstandard MIME type for PDFs, so fall back to the extension
  const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
  if (!isPdf) return compressToJpeg(file);
  // PDFs can't be compressed in the browser, so they're uploaded as is
  if (file.size > MAX_PDF_SIZE) throw new Error("PDF must be smaller than 5MB");
  return file;
}

export const useFileUpload = (selectedEmployee: Employee | null | undefined) => {
  const [uploadFile] = useUploadFileMutation();
  const [upload] = useUploadMutation();
  const [isUploading, setIsUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function fileAsync(event: React.ChangeEvent<HTMLInputElement>): Promise<Upload[]> {
    if (event.target.files == null) {
      console.warn("event.target.files == null");
      return [];
    }

    if (!selectedEmployee) return [];

    // Handles one file, or several when the input has `multiple`
    const files = Array.from(event.target.files);
    const uploadedFiles: Upload[] = [];

    setError(null);
    setIsUploading(true);
    try {
      for (const file of files) {
        const prepared = await prepareFile(file);
        const data = new FormData();
        data.append("file", prepared);
        const res1 = await uploadFile(data).unwrap();
        const res2 = await upload({
          filename: res1.filename,
          original_filename: prepared.name,
          employee_id: selectedEmployee.id,
        }).unwrap();
        uploadedFiles.push(res2);
      }
    } catch (error) {
      if (error instanceof Error) {
        // Thrown by prepareFile, e.g. PDF too large
        setError(error.message);
      } else {
        // API errors from unwrap() are { status, data } and are also shown by the ErrorToaster
        const apiError = (error as { data?: Partial<ApiError> }).data;
        setError(apiError?.description ?? "Upload failed");
      }
    } finally {
      setIsUploading(false);
      // Clear the input so picking the same file again (e.g. to retry) still fires onChange
      event.target.value = "";
    }

    // Keep any files that uploaded before an error
    return uploadedFiles;
  }

  return {
    fileAsync,
    isUploading,
    error,
  };
};
